import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Code,
  Group,
  Progress,
  Pagination,
  Radio,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconEdit,
  IconHistory,
  IconPlus,
  IconPower,
  IconRefresh,
  IconSend,
  IconTrash,
} from '@/components/icons'
import {
  ActionButton,
  AppModal,
  EmptyState,
  PageCenterLoader,
  PageShell,
  PageToolbar,
} from '@/components/ui'
import './EmergencyModePage.css'
import { ProjectIcon } from '@/components/sidebar/ProjectIcon'
import { emergencyCompletionLabel, emergencyIncidentMetrics } from '@/lib/emergency-display'
import { api, ApiError } from '@/lib/api'
import { notifyTicketCountsStale } from '@/lib/projects-events'
import { formatDateTimeDot } from '@/lib/ticket-display'
import {
  EmergencyTagFilterPanel,
  tagFilterModeFromEmergency,
  tagFilterPayload,
  type EmergencyTagFilterMode,
} from '@/components/emergency/EmergencyTagFilterPanel'
import { prepareEmergencyTemplates, templateScope, useEmergencyTemplates } from '@/components/emergency/useEmergencyTemplates'
import { EmergencyCurrentSettings } from '@/components/emergency/EmergencyCurrentSettings'
import type {
  EmergencyModeHistoryRow,
  EmergencyIncidentHistory,
  EmergencyDisableStatus,
  EmergencyCustomTemplate,
  EmergencyModeWithStats,
  ProjectRow,
  ProjectTagRow,
  TagRuleRow,
} from '@/types/api'

function formatDurationMinutes(minutes: number | null | undefined): string {
  if (minutes == null || minutes < 0) return '—'
  if (minutes < 60) return `${minutes} мин`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m > 0 ? `${h} ч ${m} мин` : `${h} ч`
}

function toLocalDateTimeInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  const yyyy = d.getFullYear()
  const mm = pad(d.getMonth() + 1)
  const dd = pad(d.getDate())
  const hh = pad(d.getHours())
  const mi = pad(d.getMinutes())
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`
}

function sameTagIds(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const sa = [...a].sort()
  const sb = [...b].sort()
  return sa.every((v, i) => v === sb[i])
}

function tagsUsedByActiveRules(tags: ProjectTagRow[], rules: TagRuleRow[]): ProjectTagRow[] {
  const ruleTagIds = new Set(
    (Array.isArray(rules) ? rules : [])
      .filter((rule) => rule.is_active !== false)
      .map((rule) => String(rule.tag_id || '').trim())
      .filter(Boolean),
  )
  return (Array.isArray(tags) ? tags : [])
    .filter((tag) => tag.is_active !== false && ruleTagIds.has(tag.id))
    .sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id, 'ru'))
}

function emergencyHistoryActionLabel(action: string, message?: string | null): string {
  const act = String(action || '').trim().toLowerCase()
  if (act === 'enabled') return 'Включена'
  if (act === 'disabled') return 'Завершена'
  if (act === 'unassigned_applied') return 'Не назначенные'
  if (act === 'message_sent') return 'Отправка сообщения'
  if (act === 'updated') {
    const msg = String(message || '')
    if (msg.includes('не назначенные')) return 'Не назначенные'
    return 'Изменены настройки'
  }
  return action || '—'
}

function emergencyHistoryActionColor(action: string, message?: string | null): string {
  const act = String(action || '').trim().toLowerCase()
  if (act === 'enabled') return 'orange'
  if (act === 'disabled') return 'gray'
  if (act === 'unassigned_applied') return 'teal'
  if (act === 'message_sent') return 'grape'
  if (act === 'updated' && String(message || '').includes('не назначенные')) return 'teal'
  if (act === 'updated') return 'blue'
  return 'gray'
}

function emergencyHistoryUserLabel(name?: string | null, email?: string | null): string {
  const n = String(name || '').trim()
  const e = String(email || '').trim()
  if (n && e && !n.toLowerCase().includes(e.split('@')[0]?.toLowerCase() || '')) {
    return `${n} (${e})`
  }
  return n || e || '—'
}

function DisableJobErrorsNotice({
  errors,
  errorText,
}: {
  errors: number
  errorText?: string | null
}) {
  const errCount = Math.max(0, Number(errors || 0))
  const text = String(errorText || '').trim()
  if (errCount <= 0 && !text) return null
  return (
    <Alert icon={<IconAlertTriangle size={14} stroke={1.5} />} color="red" variant="light" mt="sm" p="xs">
      {errCount > 0 ? (
        <Text size="xs" fw={600} mb={text ? 4 : 0}>
          Ошибок при обработке: {errCount}
        </Text>
      ) : null}
      {text ? (
        <Text size="xs" component="pre" style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit' }}>
          {text}
        </Text>
      ) : null}
    </Alert>
  )
}

export function EmergencyModePage() {
  const [projects, setProjects] = useState<ProjectRow[]>([])
  const [activeModes, setActiveModes] = useState<EmergencyModeWithStats[]>([])
  const [historyRows, setHistoryRows] = useState<EmergencyIncidentHistory[]>([])
  const [expandedHistory, setExpandedHistory] = useState<Record<string, boolean>>({})
  const [historyPage, setHistoryPage] = useState(1)
  const [historyPageSize, setHistoryPageSize] = useState(20)
  const [historyTotal, setHistoryTotal] = useState(0)
  const [historyFilter, setHistoryFilter] = useState<{ id: number; name: string } | null>(null)
  const [historyDetail, setHistoryDetail] = useState<EmergencyModeHistoryRow | null>(null)
  const historyRequest = useRef(0)
  const historySection = useRef<HTMLDivElement>(null)
  const [activePage, setActivePage] = useState(1)
  const activePageSize = 5
  const activePageCount = Math.max(1, Math.ceil(activeModes.length / activePageSize))
  const visibleActiveModes = activeModes.slice((activePage - 1) * activePageSize, activePage * activePageSize)

  useEffect(() => {
    setActivePage((page) => Math.min(page, activePageCount))
  }, [activePageCount])
  const [loadingPage, setLoadingPage] = useState(true)
  const [loadingActive, setLoadingActive] = useState(false)
  const [loadingHistory, setLoadingHistory] = useState(false)

  const [formProjectId, setFormProjectId] = useState('')
  const [message, setMessage] = useState('')
  const [note, setNote] = useState('')
  const [threadUrl, setThreadUrl] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [disableTemplateId, setDisableTemplateId] = useState('')
  const [customTemplates, setCustomTemplates] = useState<EmergencyCustomTemplate[]>([])
  const [tags, setTags] = useState<ProjectTagRow[]>([])
  const [tagFilterMode, setTagFilterMode] = useState<EmergencyTagFilterMode>('all')
  const [tagFilterIds, setTagFilterIds] = useState<string[]>([])
  const [emergencyStartedAtInput, setEmergencyStartedAtInput] = useState<string>('')
  const [enableIncludeUnassigned, setEnableIncludeUnassigned] = useState(false)
  const [loadingProjectData, setLoadingProjectData] = useState(false)
  const [enabling, setEnabling] = useState(false)
  const [templatesProject, setTemplatesProject] = useState<{ id: string; name: string } | null>(null)
  const [customTemplatesSaving, setCustomTemplatesSaving] = useState(false)

  const [varsOpen, setVarsOpen] = useState(false)
  const [variables, setVariables] = useState<Record<string, string>>({})
  const [varsLoading, setVarsLoading] = useState(false)

  const [disableModal, setDisableModal] = useState<{ projectId: string; projectName: string } | null>(null)
  const [completionMessage, setCompletionMessage] = useState('')
  const [disableAction, setDisableAction] = useState<'resolve' | 'close' | 'open' | null>(null)
  const [disabling, setDisabling] = useState(false)
  const [resumingDisable, setResumingDisable] = useState(false)
  const [disableProgress, setDisableProgress] = useState<EmergencyDisableStatus | null>(null)

  const [editModal, setEditModal] = useState<EmergencyModeWithStats | null>(null)
  const [editMessage, setEditMessage] = useState('')
  const [editNote, setEditNote] = useState('')
  const [editThreadUrl, setEditThreadUrl] = useState('')
  const [editTagFilterMode, setEditTagFilterMode] = useState<EmergencyTagFilterMode>('all')
  const [editTagFilterIds, setEditTagFilterIds] = useState<string[]>([])
  const [editTags, setEditTags] = useState<ProjectTagRow[]>([])
  const [editTagsLoading, setEditTagsLoading] = useState(false)
  const [editSaving, setEditSaving] = useState(false)
  const [editApplyingUnassigned, setEditApplyingUnassigned] = useState(false)

  const [sendMessageModal, setSendMessageModal] = useState<{ projectId: string; projectName: string } | null>(null)
  const [sendMessageText, setSendMessageText] = useState('')
  const [sendMessageTemplateId, setSendMessageTemplateId] = useState('')
  const [sendMessageSaving, setSendMessageSaving] = useState(false)

  const projectsById = useMemo(() => new Map(projects.map((p) => [p.id, p] as const)), [projects])
  const enableTemplates = useEmergencyTemplates(formProjectId || null)
  const completionTemplates = useEmergencyTemplates(disableModal?.projectId || null)
  const intermediateTemplates = useEmergencyTemplates(sendMessageModal?.projectId || null)
  const editorTemplates = useEmergencyTemplates(templatesProject?.id || null)

  useEffect(() => {
    if (templatesProject && !editorTemplates.loading && !editorTemplates.error) setCustomTemplates(editorTemplates.templates)
  }, [templatesProject, editorTemplates.templates, editorTemplates.loading, editorTemplates.error])

  const enableCustomTemplates = useMemo(
    () => enableTemplates.templates.filter((t) => templateScope(t) === 'enable'),
    [enableTemplates.templates],
  )
  const disableCustomTemplates = useMemo(
    () => completionTemplates.templates.filter((t) => templateScope(t) === 'disable'),
    [completionTemplates.templates],
  )
  const sendMessageCustomTemplates = useMemo(
    () => intermediateTemplates.templates.filter((t) => templateScope(t) === 'message'),
    [intermediateTemplates.templates],
  )
  const editorEnableTemplates = customTemplates.filter((t) => templateScope(t) === 'enable')
  const editorDisableTemplates = customTemplates.filter((t) => templateScope(t) === 'disable')
  const editorMessageTemplates = customTemplates.filter((t) => templateScope(t) === 'message')

  const openTemplates = (id: string, name: string) => {
    setCustomTemplates([])
    setTemplatesProject({ id, name })
    editorTemplates.reload()
  }

  const loadActive = useCallback(async () => {
    setLoadingActive(true)
    try {
      const list = await api.emergencyActiveWithStats()
      const modes = Array.isArray(list) ? list : []
      setActiveModes(modes)
      const inProgress = modes.find((m) => {
        const st = String(m.disable_job_status || '').toLowerCase()
        return st === 'queued' || st === 'processing' || st === 'failed'
      })
      if (inProgress) {
        setDisableProgress((prev) => {
          if (prev?.project_id && prev.project_id !== inProgress.project_id) return prev
          return {
            status: String(inProgress.disable_job_status || 'idle').toLowerCase(),
            project_id: inProgress.project_id,
            total: Number(inProgress.disable_job_total || 0),
            processed: Number(inProgress.disable_job_processed || 0),
            messages_sent: Number(inProgress.disable_job_messages_sent || 0),
            errors: Number(inProgress.disable_job_errors || 0),
            error_text: inProgress.disable_job_error_text || null,
          }
        })
      }
    } catch {
      setActiveModes([])
      showNotification({ title: 'Ошибка', message: 'Не удалось загрузить активные режимы', color: 'red' })
    } finally {
      setLoadingActive(false)
    }
  }, [])

  const loadHistory = useCallback(async (quiet = false) => {
    const request = ++historyRequest.current
    if (!quiet) setLoadingHistory(true)
    try {
      const result = await api.emergencyIncidentHistory({
        limit: historyPageSize,
        offset: (historyPage - 1) * historyPageSize,
        emergency_mode_id: historyFilter?.id,
      })
      if (request !== historyRequest.current) return
      const lastPage = Math.max(1, Math.ceil(result.total / historyPageSize))
      setHistoryTotal(result.total)
      if (historyPage > lastPage) {
        setHistoryPage(lastPage)
        return
      }
      setHistoryRows(result.items)
    } catch {
      if (request !== historyRequest.current) return
      setHistoryRows([])
      showNotification({ title: 'Ошибка', message: 'Не удалось загрузить историю', color: 'red' })
    } finally {
      if (request === historyRequest.current) setLoadingHistory(false)
    }
  }, [historyPage, historyPageSize, historyFilter?.id])

  useEffect(() => {
    setHistoryRows([])
    void loadHistory()
    return () => { historyRequest.current += 1 }
  }, [loadHistory])

  const showModeHistory = (id: number, name: string) => {
    setHistoryPage(1)
    setHistoryFilter({ id, name })
    historySection.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const hasActiveHistory = activeModes.length > 0
  const hasClosingHistory = activeModes.some((mode) => ['queued', 'processing', 'failed'].includes(mode.disable_job_status || ''))
  useEffect(() => {
    if (!hasActiveHistory || hasClosingHistory) return
    let alive = true
    let timer: ReturnType<typeof setTimeout>
    const refresh = async () => {
      if (!document.hidden) await Promise.all([loadActive(), loadHistory(true)])
      if (alive) timer = setTimeout(() => void refresh(), 30_000)
    }
    timer = setTimeout(() => void refresh(), 30_000)
    return () => { alive = false; clearTimeout(timer) }
  }, [hasActiveHistory, hasClosingHistory, loadActive, loadHistory])

  const initialLoad = useCallback(async () => {
    setLoadingPage(true)
    try {
      const [projList] = await Promise.all([
        api.projects(),
        loadActive(),
      ])
      setProjects(Array.isArray(projList) ? projList : [])
    } catch {
      setProjects([])
      showNotification({ title: 'Ошибка', message: 'Не удалось загрузить проекты', color: 'red' })
    } finally {
      setLoadingPage(false)
    }
  }, [loadActive])

  useEffect(() => {
    void initialLoad()
  }, [initialLoad])

  useEffect(() => {
    const hasDisableInProgress = activeModes.some((m) => {
      const st = String(m.disable_job_status || '').toLowerCase()
      return st === 'queued' || st === 'processing' || st === 'failed'
    })
    if (!hasDisableInProgress) return
    const timer = window.setInterval(() => {
      void loadActive()
      void loadHistory(true)
    }, 3000)
    return () => window.clearInterval(timer)
  }, [activeModes, loadActive, loadHistory])

  useEffect(() => {
    const st = String(disableProgress?.status || '').toLowerCase()
    if (!disableProgress?.project_id || (st !== 'queued' && st !== 'processing' && st !== 'failed')) return
    let alive = true
    const tick = async () => {
      try {
        const d = await api.emergencyDisableStatus(disableProgress.project_id)
        if (!alive) return
        setDisableProgress(d)
        if (String(d.status || '').toLowerCase() !== 'queued' && String(d.status || '').toLowerCase() !== 'processing' && String(d.status || '').toLowerCase() !== 'failed') {
          void loadActive()
          void loadHistory()
        }
      } catch {
        // no-op
      }
    }
    const t = window.setInterval(() => {
      void tick()
    }, 2000)
    void tick()
    return () => {
      alive = false
      window.clearInterval(t)
    }
  }, [disableProgress?.project_id, disableProgress?.status, loadActive, loadHistory])

  useEffect(() => {
    setMessage('')
    if (!formProjectId) {
      setTags([])
      setTemplateId('')
      setTagFilterMode('all')
      setTagFilterIds([])
      setEnableIncludeUnassigned(false)
      setNote('')
      setThreadUrl('')
      return
    }
    let alive = true
    setLoadingProjectData(true)
    setTemplateId('')
    setTagFilterMode('all')
    setTagFilterIds([])
    setEnableIncludeUnassigned(false)
    void Promise.all([
      api.projectTags(formProjectId),
      api.tagRulesList(formProjectId),
    ])
      .then(([tagList, ruleList]) => {
        if (!alive) return
        setTags(tagsUsedByActiveRules(Array.isArray(tagList) ? tagList : [], Array.isArray(ruleList) ? ruleList : []))
      })
      .catch(() => {
        if (!alive) return
        setTags([])
      })
      .finally(() => {
        if (alive) setLoadingProjectData(false)
      })
    return () => {
      alive = false
    }
  }, [formProjectId])

  const activeEmergencyProjectIds = useMemo(
    () => new Set(activeModes.map((m) => m.project_id)),
    [activeModes],
  )

  useEffect(() => {
    if (formProjectId && activeEmergencyProjectIds.has(formProjectId)) {
      setFormProjectId('')
    }
  }, [formProjectId, activeEmergencyProjectIds])

  const templateSelectData = useMemo(() => {
    const opts = [{ value: '', label: 'Шаблон…' }]
    for (const t of enableCustomTemplates) {
      opts.push({
        value: `custom:${t.id}`,
        label: t.name,
      })
    }
    return opts
  }, [enableCustomTemplates])

  const disableTemplateSelectData = useMemo(
    () => [
      { value: '', label: 'Шаблон сообщения…' },
      ...disableCustomTemplates.map((t) => ({ value: t.id, label: t.name })),
    ],
    [disableCustomTemplates],
  )

  const sendMessageTemplateSelectData = useMemo(
    () => [
      { value: '', label: 'Шаблон сообщения…' },
      ...sendMessageCustomTemplates.map((t) => ({ value: t.id, label: t.name })),
    ],
    [sendMessageCustomTemplates],
  )

  const projectSelectData = useMemo(() => {
    const activeIds = new Set(activeModes.map((m) => m.project_id))
    const opts = [{ value: '', label: 'Выберите проект…' }]
    for (const p of projects) {
      if (activeIds.has(p.id)) continue
      opts.push({ value: p.id, label: p.name || p.slug || p.id })
    }
    return opts
  }, [projects, activeModes])

  const enableProjectBlocked = !!formProjectId && activeEmergencyProjectIds.has(formProjectId)
  const noProjectsAvailableForEnable = projectSelectData.length <= 1

  const onTemplateChange = (id: string) => {
    setTemplateId(id)
    if (!id || !id.startsWith('custom:')) return
    const customId = id.slice('custom:'.length)
    const t = enableCustomTemplates.find((x) => x.id === customId)
    if (t?.content != null) setMessage(t.content)
  }

  const onDisableTemplateChange = (id: string) => {
    setDisableTemplateId(id)
    if (!id) return
    const t = disableCustomTemplates.find((x) => x.id === id)
    if (t?.content != null) setCompletionMessage(t.content)
  }

  const addCustomTemplate = (scope: 'enable' | 'disable' | 'message') => {
    const id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`
    setCustomTemplates((prev) => [...prev, { id, name: '', content: '', scope }])
  }

  const updateCustomTemplate = (id: string, patch: Partial<EmergencyCustomTemplate>) => {
    setCustomTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)))
  }

  const removeCustomTemplate = (id: string) => {
    setCustomTemplates((prev) => prev.filter((t) => t.id !== id))
  }

  const saveCustomTemplates = async () => {
    if (!templatesProject || editorTemplates.loading || editorTemplates.error) return
    const normalized = prepareEmergencyTemplates(customTemplates)
    setCustomTemplatesSaving(true)
    try {
      const saved = await api.emergencyCustomTemplatesSave(templatesProject.id, normalized)
      setCustomTemplates(Array.isArray(saved) ? saved : [])
      showNotification({ title: 'Сохранено', message: 'Аварийные шаблоны обновлены', color: 'green' })
      setTemplatesProject(null)
      setTemplateId('')
      setDisableTemplateId('')
      setSendMessageTemplateId('')
      enableTemplates.reload()
      completionTemplates.reload()
      intermediateTemplates.reload()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось сохранить аварийные шаблоны',
        color: 'red',
      })
    } finally {
      setCustomTemplatesSaving(false)
    }
  }

  const openVariables = async () => {
    setVarsOpen(true)
    if (Object.keys(variables).length > 0) return
    setVarsLoading(true)
    try {
      const v = await api.emergencyVariables()
      setVariables(v && typeof v === 'object' ? v : {})
    } catch {
      showNotification({ title: 'Ошибка', message: 'Не удалось загрузить переменные', color: 'red' })
    } finally {
      setVarsLoading(false)
    }
  }

  const onEnable = async () => {
    const pid = formProjectId.trim()
    const msg = message.trim()
    if (!pid) {
      showNotification({ title: 'Форма', message: 'Выберите проект', color: 'yellow' })
      return
    }
    if (activeEmergencyProjectIds.has(pid)) {
      showNotification({
        title: 'Форма',
        message: 'Для этого проекта аварийный режим уже включён. Используйте «Изменить» в списке выше.',
        color: 'yellow',
      })
      return
    }
    if (!msg) {
      showNotification({ title: 'Форма', message: 'Введите сообщение для пользователей', color: 'yellow' })
      return
    }
    setEnabling(true)
    try {
      const startedAtIso = emergencyStartedAtInput ? new Date(emergencyStartedAtInput).toISOString() : undefined
      const tagPayload = tagFilterPayload(tagFilterMode, tagFilterIds)
      await api.emergencyEnable({
        project_id: pid,
        message: msg,
        note: note.trim() || undefined,
        thread_url: threadUrl.trim() || undefined,
        excluded_tag_ids: tagPayload.excluded_tag_ids.length ? tagPayload.excluded_tag_ids : undefined,
        included_tag_ids: tagPayload.included_tag_ids.length ? tagPayload.included_tag_ids : undefined,
        ticket_scope: enableIncludeUnassigned ? 'unassigned' : 'all',
        started_at: enableIncludeUnassigned ? undefined : startedAtIso,
      })
      showNotification({ title: 'Включено', message: 'Аварийный режим включён', color: 'green' })
      setMessage('')
      setNote('')
      setThreadUrl('')
      setTemplateId('')
      setTagFilterMode('all')
      setTagFilterIds([])
      setEnableIncludeUnassigned(false)
      setEmergencyStartedAtInput('')
      notifyTicketCountsStale()
      void loadActive()
      void loadHistory()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось включить режим',
        color: 'red',
      })
    } finally {
      setEnabling(false)
    }
  }

  const openDisable = (projectId: string, projectName: string) => {
    setDisableModal({ projectId, projectName })
    setCompletionMessage('')
    setDisableTemplateId('')
    setDisableAction(null)
  }

  const openSendMessage = (projectId: string, projectName: string) => {
    setSendMessageModal({ projectId, projectName })
    setSendMessageText('')
    setSendMessageTemplateId('')

  }

  const onSendMessageTemplateChange = (id: string) => {
    setSendMessageTemplateId(id)
    if (!id) return
    const t = sendMessageCustomTemplates.find((x) => x.id === id)
    if (t?.content != null) setSendMessageText(t.content)
  }

  const openEdit = (mode: EmergencyModeWithStats) => {
    const filterMode = tagFilterModeFromEmergency(mode.excluded_tag_ids, mode.included_tag_ids)
    const filterIds =
      filterMode === 'include'
        ? Array.isArray(mode.included_tag_ids)
          ? mode.included_tag_ids
          : []
        : filterMode === 'exclude'
          ? Array.isArray(mode.excluded_tag_ids)
            ? mode.excluded_tag_ids
            : []
          : []
    setEditModal(mode)
    setEditMessage(mode.message || '')
    setEditNote(mode.note || '')
    setEditThreadUrl(mode.thread_url || '')
    setEditTagFilterMode(filterMode)
    setEditTagFilterIds(filterIds)
    setEditTags([])
    setEditTagsLoading(true)
    void Promise.all([api.projectTags(mode.project_id), api.tagRulesList(mode.project_id)])
      .then(([tagList, ruleList]) =>
        setEditTags(tagsUsedByActiveRules(Array.isArray(tagList) ? tagList : [], Array.isArray(ruleList) ? ruleList : [])),
      )
      .catch(() => setEditTags([]))
      .finally(() => setEditTagsLoading(false))
  }

  const onSaveEdit = async () => {
    if (!editModal) return
    const msg = editMessage.trim()
    if (!msg) {
      showNotification({ title: 'Форма', message: 'Введите сообщение для пользователей', color: 'yellow' })
      return
    }
    setEditSaving(true)
    try {
      const tagPayload = tagFilterPayload(editTagFilterMode, editTagFilterIds)
      const originalMode = tagFilterModeFromEmergency(editModal.excluded_tag_ids, editModal.included_tag_ids)
      const originalIds =
        originalMode === 'include'
          ? Array.isArray(editModal.included_tag_ids)
            ? editModal.included_tag_ids
            : []
          : originalMode === 'exclude'
            ? Array.isArray(editModal.excluded_tag_ids)
              ? editModal.excluded_tag_ids
              : []
            : []
      const tagsChanged =
        editTagFilterMode !== originalMode || !sameTagIds(editTagFilterIds, originalIds)

      await api.emergencyUpdate(editModal.project_id, {
        message: msg,
        note: editNote.trim(),
        thread_url: editThreadUrl.trim(),
        ...(tagsChanged
          ? {
              excluded_tag_ids: tagPayload.excluded_tag_ids,
              included_tag_ids: tagPayload.included_tag_ids,
            }
          : {}),
      })
      showNotification({
        title: 'Сохранено',
        message: 'Настройки аварии обновлены. Изменения применятся к новым обращениям.',
        color: 'green',
      })
      setEditModal(null)
      notifyTicketCountsStale()
      void loadActive()
      void loadHistory()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось сохранить изменения',
        color: 'red',
      })
    } finally {
      setEditSaving(false)
    }
  }

  const onApplyUnassigned = async () => {
    if (!editModal) return
    setEditApplyingUnassigned(true)
    try {
      const tagPayload = tagFilterPayload(editTagFilterMode, editTagFilterIds)
      const originalMode = tagFilterModeFromEmergency(editModal.excluded_tag_ids, editModal.included_tag_ids)
      const originalIds =
        originalMode === 'include'
          ? Array.isArray(editModal.included_tag_ids)
            ? editModal.included_tag_ids
            : []
          : originalMode === 'exclude'
            ? Array.isArray(editModal.excluded_tag_ids)
              ? editModal.excluded_tag_ids
              : []
            : []
      const tagsChanged =
        editTagFilterMode !== originalMode || !sameTagIds(editTagFilterIds, originalIds)

      if (tagsChanged) {
        await api.emergencyUpdate(editModal.project_id, {
          excluded_tag_ids: tagPayload.excluded_tag_ids,
          included_tag_ids: tagPayload.included_tag_ids,
        })
      }

      const res = await api.emergencyApplyUnassigned(editModal.project_id)
      const processed = Number(res.result?.tickets_processed ?? 0)
      const total = Number(res.result?.tickets_total ?? 0)
      showNotification({
        title: 'Готово',
        message: `Добавлено в аварию: ${processed} из ${total} не назначенных обращений`,
        color: 'green',
      })
      if (res.emergency_mode) {
        const mode = res.emergency_mode
        setEditModal(mode)
        const filterMode = tagFilterModeFromEmergency(mode.excluded_tag_ids, mode.included_tag_ids)
        setEditTagFilterMode(filterMode)
        setEditTagFilterIds(
          filterMode === 'include'
            ? Array.isArray(mode.included_tag_ids)
              ? mode.included_tag_ids
              : []
            : filterMode === 'exclude'
              ? Array.isArray(mode.excluded_tag_ids)
                ? mode.excluded_tag_ids
                : []
              : [],
        )
      }
      notifyTicketCountsStale()
      void loadActive()
      void loadHistory()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось добавить не назначенные обращения',
        color: 'red',
      })
    } finally {
      setEditApplyingUnassigned(false)
    }
  }

  const onResumeDisable = async (projectId: string, projectName: string) => {
    setResumingDisable(true)
    try {
      const res = await api.emergencyDisableResume(projectId)
      const rs = (res as { result?: Record<string, unknown> })?.result || {}
      const rStatus = String(rs.status || 'queued').toLowerCase()
      setDisableProgress({
        status: rStatus,
        project_id: projectId,
        total: Number(rs.total || 0),
        processed: Number(rs.processed || 0),
        messages_sent: Number(rs.messages_sent || 0),
        errors: Number(rs.errors || 0),
        remaining: Number(rs.remaining || 0),
        error_text: typeof rs.error_text === 'string' ? rs.error_text : null,
      })
      showNotification({
        title: 'Продолжено',
        message: `Выключение аварии для «${projectName}» поставлено в очередь`,
        color: 'green',
      })
      void loadActive()
      void loadHistory()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось продолжить выключение',
        color: 'red',
      })
    } finally {
      setResumingDisable(false)
    }
  }

  const onConfirmDisable = async () => {
    if (!disableModal) return
    const msg = completionMessage.trim()
    if (!msg) {
      showNotification({ title: 'Форма', message: 'Введите сообщение для пользователей', color: 'yellow' })
      return
    }
    if (!disableAction) {
      showNotification({ title: 'Форма', message: 'Выберите действие с обращениями', color: 'yellow' })
      return
    }
    setDisabling(true)
    try {
      const res = await api.emergencyDisable({
        project_id: disableModal.projectId,
        completion_message: msg,
        action: disableAction,
      })
      const rs = (res as { result?: Record<string, unknown> })?.result || {}
      const rStatus = String(rs.status || '').toLowerCase()
      if (rStatus === 'queued' || rStatus === 'processing') {
        setDisableProgress({
          status: rStatus,
          project_id: disableModal.projectId,
          total: Number(rs.total || 0),
          processed: Number(rs.processed || 0),
          messages_sent: Number(rs.messages_sent || 0),
          errors: Number(rs.errors || 0),
          error_text: typeof rs.error_text === 'string' ? rs.error_text : null,
        })
      }
      showNotification({ title: 'Выключено', message: 'Аварийный режим выключен', color: 'green' })
      setDisableModal(null)
      notifyTicketCountsStale()
      void loadActive()
      void loadHistory()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось выключить режим',
        color: 'red',
      })
    } finally {
      setDisabling(false)
    }
  }

  const onConfirmSendMessage = async () => {
    if (!sendMessageModal) return
    const msg = sendMessageText.trim()
    if (!msg) {
      showNotification({ title: 'Форма', message: 'Введите текст сообщения', color: 'yellow' })
      return
    }
    setSendMessageSaving(true)
    try {
      const res = await api.emergencySendMessage(sendMessageModal.projectId, { message: msg })
      const ticketsCount = res?.result?.tickets_count ?? 0
      showNotification({
        title: 'Отправлено',
        message: `Сообщение отправлено по обращениям: ${ticketsCount}`,
        color: 'green',
      })
      setSendMessageModal(null)
      void loadHistory()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось отправить сообщение',
        color: 'red',
      })
    } finally {
      setSendMessageSaving(false)
    }
  }

  if (loadingPage) {
    return (
      <PageShell>
        <PageCenterLoader label="Загрузка" minHeight={240} />
      </PageShell>
    )
  }

  return (
    <PageShell>
      <Stack gap="md" className="emergency-page">
        <PageToolbar
          leading={
            <Box>
              <Group gap="xs" wrap="wrap">
                <IconAlertTriangle size={20} stroke={1.6} />
                <Text fw={700} size="lg">
                  Аварийный режим
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                Управление массовыми инцидентами, шаблонами сообщений и обращениями, которые попали в аварию.
              </Text>
            </Box>
          }
        />

        <SimpleGrid cols={{ base: 1, xl: 2 }} spacing="md" verticalSpacing="md" style={{ alignItems: 'start' }}>
        <Card withBorder padding="md" radius="md">
          <PageToolbar
            leading={
              <Box>
                <Text fw={600}>Активные аварии</Text>
                <Text size="xs" c="dimmed">
                  Проекты, где сейчас действует аварийная логика.
                </Text>
              </Box>
            }
            trailing={
              <ActionIcon
                variant="default"
                aria-label="Обновить"
                title="Обновить"
                loading={loadingActive}
                onClick={() => void loadActive()}
              >
                <IconRefresh size={16} stroke={1.5} />
              </ActionIcon>
            }
          />
          {loadingActive && activeModes.length === 0 ? (
            <PageCenterLoader label="Загрузка…" minHeight={120} />
          ) : activeModes.length === 0 ? (
            <EmptyState
              title="Режим нигде не включён"
              description="Активных аварийных режимов по workspace нет."
              icon={<IconCircleCheck size={32} stroke={1.2} style={{ color: 'var(--mantine-color-green-6)' }} />}
            />
          ) : (
            <Stack gap="sm" mt="sm" className="emergency-active-list">
              <Box component="ul" className="emergency-list">
              {visibleActiveModes.map((m) => {
                const projName = m.project_name?.trim() || projectsById.get(m.project_id)?.name || projectsById.get(m.project_id)?.slug || '—'
                return (
                  <Box component="li" key={m.id} className="emergency-active-row">
                    <Stack gap="sm">
                      <Group gap="xs" wrap="wrap">
                        <ProjectIcon project={projectsById.get(m.project_id) || { name: projName, color: '', icon: null, icon_url: null }} boxSize={20} emergency />
                        <Text fw={600} size="sm" style={{ flex: 1 }}>{projName}</Text>
                        <Badge color="orange" variant="light">Активна</Badge>
                      </Group>
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}><Text span c="dimmed">Комментарий: </Text>{m.note || 'Не указан'}</Text>
                      {m.thread_url ? <Text size="sm" component="a" href={m.thread_url} target="_blank" rel="noreferrer" c="blue">Тред: {m.thread_url}</Text> : null}
                      <div className="emergency-metadata">
                        <Badge size="sm" color="orange" variant="filled">Сейчас в аварии: {m.tickets_count ?? 0}</Badge>
                        <Badge size="sm" color="blue" variant="light">Длительность режима: {formatDurationMinutes(m.duration_minutes)}</Badge>
                        <Text size="xs"><Text span c="dimmed">Режим включён: </Text>{formatDateTimeDot(m.created_at)}</Text>
                        <Text size="xs"><Text span c="dimmed">Начало аварии: </Text>{m.started_at ? formatDateTimeDot(m.started_at) : 'Не указано'}</Text>
                        <Text size="xs"><Text span c="dimmed">Кто включил: </Text>{emergencyHistoryUserLabel(m.created_by_name, m.created_by_email)}</Text>
                      </div>
                      <Divider />
                          {Number(m.disable_job_errors || 0) > 0 ? (
                            <Badge size="xs" color="red" variant="light" mb={4}>
                              Ошибки: {m.disable_job_errors}
                            </Badge>
                          ) : null}
                          <Group gap={6} className="emergency-actions" wrap="wrap">
                            <Button
                              size="compact-xs"
                              variant="light"
                              leftSection={<IconSend size={14} stroke={1.5} />}
                              onClick={() => openSendMessage(m.project_id, projName)}
                            >
                              Отправить сообщение
                            </Button>
                            <Button
                              size="compact-xs"
                              variant="light"
                              leftSection={<IconEdit size={14} stroke={1.5} />}
                              onClick={() => openEdit(m)}
                            >
                              Изменить
                            </Button>
                            {(m.disable_job_can_resume || String(m.disable_job_status || '').toLowerCase() === 'failed') ? (
                              <Button
                                size="compact-xs"
                                variant="light"
                                color="orange"
                                loading={resumingDisable}
                                onClick={() => void onResumeDisable(m.project_id, projName)}
                              >
                                Продолжить
                              </Button>
                            ) : null}
                            <Button
                              size="compact-xs"
                              variant="default"
                              leftSection={<IconPower size={14} stroke={1.5} />}
                              disabled={
                                (String(m.disable_job_status || '').toLowerCase() === 'queued' ||
                                  String(m.disable_job_status || '').toLowerCase() === 'processing') &&
                                !m.disable_job_can_restart
                              }
                              onClick={() => openDisable(m.project_id, projName)}
                            >
                              {m.disable_job_can_restart &&
                              (String(m.disable_job_status || '').toLowerCase() === 'processing' ||
                                String(m.disable_job_status || '').toLowerCase() === 'queued')
                                ? 'Перезапустить'
                                : 'Выключить'}
                            </Button>
                            <Button size="compact-xs" variant="default" onClick={() => openTemplates(m.project_id, projName)}>Шаблоны</Button>
                            <Button size="compact-xs" variant="default" leftSection={<IconHistory size={14} />} onClick={() => showModeHistory(m.id, projName)}>
                              История действий
                            </Button>
                          </Group>
                    </Stack>
                  </Box>
                )
              })}
              </Box>
              {activePageCount > 1 ? (
                <Stack gap={6}>
                  <Text size="xs" c="dimmed">Всего активных аварий: {activeModes.length}</Text>
                  <Pagination value={activePage} onChange={setActivePage} total={activePageCount} size="sm" siblings={0} className="emergency-pagination" />
                </Stack>
              ) : null}
            </Stack>
          )}
          {activeModes.map((m) => {
            const st = String(m.disable_job_status || '').toLowerCase()
            if (st !== 'queued' && st !== 'processing' && st !== 'failed') return null
            const total = Math.max(0, Number(m.disable_job_total || 0))
            const processed = Math.max(0, Number(m.disable_job_processed || 0))
            const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0
            const projName =
              m.project_name?.trim() ||
              projectsById.get(m.project_id)?.name ||
              projectsById.get(m.project_id)?.slug ||
              m.project_id
            return (
              <Card key={`disable-progress-${m.id}`} withBorder mt="sm" p="sm" radius="md">
                <Group justify="space-between" mb={6}>
                  <Text size="sm" fw={600}>
                    {st === 'failed' ? 'Выключение аварии прервано' : 'Выключение аварии'}: {projName}
                  </Text>
                  <Text size="xs" c="dimmed">{processed}/{total}</Text>
                </Group>
                <Progress value={pct} size="md" radius="xl" color={st === 'failed' ? 'orange' : 'blue'} />
                <Group justify="space-between" mt={6}>
                  <Text size="xs" c="dimmed">Отправлено: {m.disable_job_messages_sent || 0}</Text>
                  <Text size="xs" c={Number(m.disable_job_errors || 0) > 0 ? 'red' : 'dimmed'}>
                    Ошибки: {m.disable_job_errors || 0}
                  </Text>
                </Group>
                <DisableJobErrorsNotice
                  errors={Number(m.disable_job_errors || 0)}
                  errorText={m.disable_job_error_text}
                />
                {st === 'failed' || m.disable_job_can_resume ? (
                  <Group justify="flex-end" mt="sm">
                    <Button
                      size="compact-sm"
                      variant="light"
                      color="orange"
                      loading={resumingDisable}
                      onClick={() => void onResumeDisable(m.project_id, projName)}
                    >
                      Продолжить с {processed} обращений
                    </Button>
                  </Group>
                ) : null}
              </Card>
            )
          })}
        </Card>

        <Card withBorder padding="md" radius="md">
          <Group justify="space-between" align="flex-start" wrap="wrap" mb="md">
            <Box>
              <Text fw={600}>Включить аварийный режим</Text>
              <Text size="xs" c="dimmed">
                Настройте проект, сообщение, время старта и правила попадания обращений.
              </Text>
            </Box>
            {formProjectId ? (
              <Badge variant="light" color="orange">
                {projectsById.get(formProjectId)?.name || projectsById.get(formProjectId)?.slug || 'Проект выбран'}
              </Badge>
            ) : null}
          </Group>
          <Stack gap="md">
            {noProjectsAvailableForEnable ? (
              <Alert icon={<IconAlertTriangle size={14} stroke={1.5} />} color="yellow" variant="light">
                <Text size="sm">
                  Аварийный режим уже включён для всех доступных проектов. Чтобы включить на новом проекте,
                  сначала выключите аварию в списке выше.
                </Text>
              </Alert>
            ) : null}
            <Select
              label="Проект"
              data={projectSelectData}
              value={formProjectId}
              onChange={(value) => setFormProjectId(value || '')}
              disabled={(loadingProjectData && !!formProjectId) || noProjectsAvailableForEnable}
              searchable
              allowDeselect={false}
              checkIconPosition="right"
            />
            <div>
              <Group gap="sm" align="flex-end" wrap="wrap" mb={6}>
                <Select
                  label="Шаблон сообщения"
                  data={templateSelectData}
                  value={templateId}
                  onChange={(value) => onTemplateChange(value || '')}
                  disabled={!formProjectId || enableTemplates.loading || enableTemplates.error}
                  placeholder={enableTemplates.loading ? 'Загрузка шаблонов…' : 'Выберите шаблон'}
                  nothingFoundMessage="Нет шаблонов включения"
                  searchable
                  allowDeselect={false}
                  checkIconPosition="right"
                  styles={{ root: { flex: 1, minWidth: 0, maxWidth: 380 } }}
                />
                <Button
                  variant="default"
                  size="xs"
                  mb={2}
                  disabled={!formProjectId}
                  onClick={() => openTemplates(formProjectId, projectsById.get(formProjectId)?.name || formProjectId)}
                >
                  Аварийные шаблоны
                </Button>
                <Button variant="default" size="xs" mb={2} onClick={() => void openVariables()}>
                  Переменные
                </Button>
              </Group>
              {enableTemplates.error ? <Alert color="red" mb="xs">Не удалось загрузить шаблоны. <Button variant="subtle" size="xs" onClick={enableTemplates.reload}>Повторить</Button></Alert> : null}
              <Textarea
                label="Сообщение для пользователей"
                placeholder="Текст при создании обращения в аварийном режиме…"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                minRows={4}
              />
              <Text size="xs" c="dimmed" mt={6}>
                Плейсхолдеры: [name], [email], [ticket_number] и др. — см. «Переменные».
              </Text>
            </div>
            <Textarea
              label="Причина / заметка для анализа"
              placeholder="Что произошло, почему включаем аварию, что потом сравнить с обращениями…"
              value={note}
              onChange={(e) => setNote(e.currentTarget.value)}
              minRows={2}
            />
            <TextInput
              label="Ссылка на тред"
              placeholder="https://..."
              value={threadUrl}
              onChange={(e) => setThreadUrl(e.currentTarget.value)}
            />
            <div>
              <Text size="sm" fw={500} mb={6}>
                Время начала аварии
              </Text>
              <Group gap="xs" mb={8} wrap="wrap">
                {[5, 10, 20, 30, 60].map((m) => (
                  <Button
                    key={m}
                    size="compact-xs"
                    variant="default"
                    disabled={enableIncludeUnassigned}
                    onClick={() => setEmergencyStartedAtInput(toLocalDateTimeInputValue(new Date(Date.now() - m * 60_000)))}
                  >
                    {m} мин назад
                  </Button>
                ))}
                <Button
                  size="compact-xs"
                  variant="light"
                  disabled={enableIncludeUnassigned}
                  onClick={() => setEmergencyStartedAtInput(toLocalDateTimeInputValue(new Date()))}
                >
                  Сейчас
                </Button>
              </Group>
              <Group gap="sm" align="flex-end" wrap="wrap">
                <TextInput
                  label="Точное время"
                  type="datetime-local"
                  value={emergencyStartedAtInput}
                  onChange={(e) => setEmergencyStartedAtInput(e.currentTarget.value)}
                  placeholder="Если пусто — текущий момент"
                  disabled={enableIncludeUnassigned}
                  style={{ flex: 1, minWidth: 0, maxWidth: 320 }}
                />
                <Button
                  size="sm"
                  mb={2}
                  variant={enableIncludeUnassigned ? 'filled' : 'light'}
                  color="orange"
                  onClick={() => setEnableIncludeUnassigned((v) => !v)}
                >
                  Все не назначенные
                </Button>
              </Group>
              <Text size="xs" c="dimmed" mt={6}>
                {enableIncludeUnassigned
                  ? 'В аварию попадут неназначенные обращения new/open. Фильтр по меткам ниже применяется к ним же. Время начала не используется.'
                  : 'Если указать прошлое время, в аварию попадут обращения проекта, созданные с этого момента.'}
              </Text>
            </div>
            <EmergencyTagFilterPanel
              mode={tagFilterMode}
              onModeChange={setTagFilterMode}
              selectedTagIds={tagFilterIds}
              onSelectedTagIdsChange={setTagFilterIds}
              tags={tags}
              loading={loadingProjectData}
              noProject={!formProjectId}
            />
            <div>
              <Button
                color="red"
                leftSection={<IconAlertTriangle size={16} stroke={1.5} />}
                loading={enabling}
                disabled={noProjectsAvailableForEnable || enableProjectBlocked || !formProjectId}
                onClick={() => void onEnable()}
              >
                Включить аварийный режим
              </Button>
            </div>
          </Stack>
        </Card>
        </SimpleGrid>

        <Card ref={historySection} withBorder padding="md" radius="md">
          <PageToolbar
            leading={
              <Box>
                <Text fw={600}>История</Text>
                <Text size="xs" c="dimmed">
                  По дате включения: от новых аварий к старым. Действия каждой аварии показаны ниже по времени.
                </Text>
              </Box>
            }
            trailing={
              <ActionIcon
                variant="default"
                aria-label="Обновить историю"
                title="Обновить"
                loading={loadingHistory}
                onClick={() => void loadHistory()}
              >
                <IconRefresh size={16} stroke={1.5} />
              </ActionIcon>
            }
          />
          {historyFilter ? (
            <Group mt="sm" gap="xs" wrap="wrap">
              <Text size="sm">Действия аварии #{historyFilter.id}: {historyFilter.name}</Text>
              <Button size="compact-xs" variant="subtle" onClick={() => { setHistoryPage(1); setHistoryFilter(null) }}>Показать все аварии</Button>
            </Group>
          ) : null}
          <Group mt="sm" mb="sm" justify="space-between" wrap="wrap">
            <Text size="sm" c="dimmed">Всего аварий: {historyTotal}</Text>
            <Select label="На странице" aria-label="Аварий на странице" w={120} value={String(historyPageSize)} allowDeselect={false}
              data={['10', '20', '50', '100']} onChange={(value) => { setHistoryPage(1); setHistoryPageSize(Number(value || 20)) }} />
          </Group>
          {loadingHistory ? (
            <PageCenterLoader label="Загрузка…" minHeight={120} />
          ) : historyRows.length === 0 ? (
            <EmptyState title="История пуста" description="Действий аварийного режима пока нет." icon={<IconHistory size={32} stroke={1.2} />} />
          ) : (
            <Box className="emergency-history-list">
              <table className="emergency-history-table" aria-label="История аварий и их действий">
                <thead><tr><th>Авария и время</th><th>Состояние / действие</th><th>Комментарий, тред и сообщение</th></tr></thead>
                {historyRows.map((group) => {
                  const h = group.opening
                  const openingEvent = group.events.find((event) => event.action === 'enabled')
                  const metrics = emergencyIncidentMetrics(group, activeModes.find((mode) => mode.id === h.emergency_mode_id))
                  const { completion, completed } = metrics
                  const expanded = expandedHistory[group.key] ?? !completed
                  const events = group.events.filter((event) => event.id !== openingEvent?.id)
                  const enabledAt = h.enabled_at || openingEvent?.created_at
                  return (
                    <tbody key={group.key} className="emergency-incident-group" data-incident={group.key}>
                      <tr className="emergency-incident-heading" data-completed={completed || undefined}>
                        <td>
                          <Stack gap={4}>
                            <Group gap="xs" wrap="nowrap" align="flex-start">
                              <ProjectIcon project={projectsById.get(h.project_id) || { name: h.project_name || '—', color: '', icon: null, icon_url: null }} boxSize={20} emergency={!completed && group.is_active === true} />
                              <Text size="sm" fw={600}>{h.project_name || '—'}{h.emergency_mode_id != null ? ` · Авария #${h.emergency_mode_id}` : ' · Запись без связи с аварией'}</Text>
                            </Group>
                            <Text size="xs" c="dimmed">Включение: {enabledAt ? formatDateTimeDot(enabledAt) : 'Не сохранено'} · Начало: {h.started_at ? formatDateTimeDot(h.started_at) : 'Не сохранено'}</Text>
                            <Text size="xs" lineClamp={1} title={emergencyHistoryUserLabel(h.enabled_by_name || openingEvent?.user_name, h.enabled_by_email || openingEvent?.user_email)}>Кто включил: {emergencyHistoryUserLabel(h.enabled_by_name || openingEvent?.user_name, h.enabled_by_email || openingEvent?.user_email)}</Text>
                          </Stack>
                        </td>
                        <td>
                          <Stack gap={4}>
                            <Group gap={6}>
                              {enabledAt ? <Text size="xs" fw={600} className={completed ? 'emergency-opening-completed' : undefined}>Включена</Text> : null}
                              <Badge size="xs" variant="light" color={completed ? 'gray' : group.is_active === true ? 'orange' : 'gray'}>{completed ? 'Завершена' : group.is_active === true ? 'Активна' : 'Состояние не сохранено'}</Badge>
                            </Group>
                            <Group gap={6} className="emergency-metric-badges">
                              {completed ? <Badge color="green" variant="light" size="sm">При завершении: {metrics.finalCount ?? '—'}</Badge> : <Badge color="orange" variant="filled" size="sm">Сейчас в аварии: {metrics.currentCount ?? '—'}</Badge>}
                              <Badge color="blue" variant="light" size="sm">Длительность режима: {formatDurationMinutes(metrics.durationMinutes)}</Badge>
                              {metrics.closing ? <Badge color="violet" variant="light" size="sm">Завершение: {metrics.closing.processed}/{metrics.closing.total}</Badge> : null}
                            </Group>
                            {completion ? <Text size="xs">{emergencyCompletionLabel(completion.completion_action)} · {formatDateTimeDot(completion.created_at)}</Text> : null}
                            {events.length > 0 ? <Button size="compact-xs" variant="subtle" aria-expanded={expanded} onClick={() => setExpandedHistory((prev) => ({ ...prev, [group.key]: !expanded }))}>{expanded ? 'Скрыть действия' : 'Действия'} ({events.length})</Button> : null}
                          </Stack>
                        </td>
                        <td>
                          <Stack gap={4}>
                            <Text size="xs" lineClamp={1} title={h.note || undefined}><Text span c="dimmed">Комментарий: </Text>{h.note || 'Не указан'}</Text>
                            {h.thread_url ? <Text component="a" size="xs" href={h.thread_url} target="_blank" rel="noreferrer" c="blue">Тред аварии</Text> : <Text size="xs" c="dimmed">Тред не указан</Text>}
                            {completion || openingEvent ? <><Text size="xs" lineClamp={1} style={{ whiteSpace: 'pre-wrap' }}>{(completion || openingEvent)?.message || 'Сообщение не сохранено'}</Text><Group gap={6}><Button variant="subtle" size="compact-xs" onClick={() => setHistoryDetail((completion || openingEvent)!)}>{completion ? 'Итог аварии' : 'Подробнее о включении'}</Button>{completion && openingEvent ? <Button variant="subtle" size="compact-xs" onClick={() => setHistoryDetail(openingEvent)}>Включение</Button> : null}</Group></> : null}
                          </Stack>
                        </td>
                      </tr>
                      {expanded && events.map((event) => (
                        <tr key={event.id} className="emergency-incident-event">
                          <td>
                            <Stack gap={4} className="emergency-event-branch">
                              <Text size="sm" fw={600}>{emergencyHistoryActionLabel(event.action, event.message)}</Text>
                              <Text size="xs">{formatDateTimeDot(event.created_at)}</Text>
                              <Text size="xs" c="dimmed">{emergencyHistoryUserLabel(event.user_name, event.user_email)}</Text>
                            </Stack>
                          </td>
                          <td>
                            {event.action === 'disabled' ? <Stack gap={4}><Badge color="gray" variant="light" size="sm">Авария завершена</Badge><Text size="sm" fw={600}>{emergencyCompletionLabel(event.completion_action)}</Text><Text size="xs">Обращений: {event.tickets_count ?? '—'} · Длительность: {formatDurationMinutes(event.duration_minutes)}</Text></Stack> : <Badge size="sm" variant="light" color={emergencyHistoryActionColor(event.action, event.message)}>{emergencyHistoryActionLabel(event.action, event.message)}</Badge>}
                          </td>
                          <td><Stack gap={4}><Text size="sm" lineClamp={3} style={{ whiteSpace: 'pre-wrap' }}>{event.message || 'Сообщение не сохранено'}</Text><Button variant="light" size="compact-xs" onClick={() => setHistoryDetail(event)}>Подробнее о действии</Button></Stack></td>
                        </tr>
                      ))}
                    </tbody>
                  )
                })}
              </table>
            </Box>
          )}
          <Pagination mt="md" value={historyPage} onChange={setHistoryPage}
            total={Math.max(1, Math.ceil(historyTotal / historyPageSize))} disabled={loadingHistory}
            size="sm" siblings={0} className="emergency-pagination" />
        </Card>

        {disableProgress && ['queued', 'processing', 'failed'].includes(String(disableProgress.status || '').toLowerCase()) ? (
          <Card withBorder padding="md" radius="md">
            <Group justify="space-between" mb={6}>
              <Text size="sm" fw={600}>
                {String(disableProgress.status || '').toLowerCase() === 'failed'
                  ? 'Выключение аварии прервано'
                  : 'Выключение аварии (фоновая обработка)'}
              </Text>
              <Text size="xs" c="dimmed">
                {Math.max(0, Number(disableProgress.processed || 0))}/{Math.max(0, Number(disableProgress.total || 0))}
              </Text>
            </Group>
            <Progress
              value={
                Math.max(0, Number(disableProgress.total || 0)) > 0
                  ? Math.min(
                      100,
                      Math.round(
                        (Math.max(0, Number(disableProgress.processed || 0)) /
                          Math.max(1, Number(disableProgress.total || 0))) *
                          100,
                      ),
                    )
                  : 0
              }
              size="md"
              radius="xl"
            />
            <Group justify="space-between" mt={6}>
              <Text size="xs" c="dimmed">Отправлено: {Number(disableProgress.messages_sent || 0)}</Text>
              <Text size="xs" c={Number(disableProgress.errors || 0) > 0 ? 'red' : 'dimmed'}>
                Ошибки: {Number(disableProgress.errors || 0)}
              </Text>
            </Group>
            <DisableJobErrorsNotice
              errors={Number(disableProgress.errors || 0)}
              errorText={disableProgress.error_text}
            />
          </Card>
        ) : null}
      </Stack>

      <AppModal opened={historyDetail != null} onClose={() => setHistoryDetail(null)} title="Действие аварийного режима" size="lg" className="emergency-modal">
        {historyDetail ? (
          <Stack gap="sm" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            <Text fw={600}>{historyDetail.project_name}{historyDetail.emergency_mode_id != null ? ` · Авария #${historyDetail.emergency_mode_id}` : ''}</Text>
            <Text size="sm">{emergencyHistoryActionLabel(historyDetail.action, historyDetail.message)} · {formatDateTimeDot(historyDetail.created_at)}</Text>
            <Text size="sm">Автор: {emergencyHistoryUserLabel(historyDetail.user_name, historyDetail.user_email)}</Text>
            <Text size="sm">Кто включил: {emergencyHistoryUserLabel(historyDetail.enabled_by_name, historyDetail.enabled_by_email)}</Text>
            <Text size="sm">Режим включён: {historyDetail.enabled_at ? formatDateTimeDot(historyDetail.enabled_at) : 'Не сохранено'}</Text>
            <Text size="sm">Начало аварии: {historyDetail.started_at ? formatDateTimeDot(historyDetail.started_at) : 'Не сохранено'}</Text>
            {historyDetail.action === 'disabled' ? <Text size="sm" fw={600}>{emergencyCompletionLabel(historyDetail.completion_action)}</Text> : null}
            <Text size="sm">Обращений: {historyDetail.tickets_count ?? '—'} · Длительность: {formatDurationMinutes(historyDetail.duration_minutes)}</Text>
            <Divider />
            <Text size="sm" fw={600}>{historyDetail.action === 'disabled' ? 'Сообщение о завершении аварии' : 'Сообщение действия'}</Text>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{historyDetail.message || 'Сообщение не сохранено'}</Text>
            {historyDetail.note ? <><Text size="sm" fw={600}>Комментарий к аварии</Text><Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{historyDetail.note}</Text></> : null}
            {historyDetail.thread_url ? <Text size="sm" component="a" href={historyDetail.thread_url} target="_blank" rel="noreferrer" c="blue">Тред аварии</Text> : null}
            {historyDetail.emergency_mode_id != null ? (
              <Button variant="light" onClick={() => {
                showModeHistory(historyDetail.emergency_mode_id!, historyDetail.project_name)
                setHistoryDetail(null)
              }}>Все действия этой аварии</Button>
            ) : null}
          </Stack>
        ) : null}
      </AppModal>

      <AppModal className="emergency-modal" opened={varsOpen} onClose={() => setVarsOpen(false)} title="Переменные для сообщения" size="md">
        {varsLoading ? (
          <PageCenterLoader label="Загрузка…" minHeight={120} />
        ) : (
          <Stack gap="sm">
            <Text size="sm" c="dimmed">
              Используйте в формате{' '}
              <Code>[variable_name]</Code>:
            </Text>
            <ScrollArea h={360} type="auto">
              <Stack gap="xs">
                {Object.entries(variables).map(([k, desc]) => (
                  <Card key={k} withBorder padding="xs" radius="md">
                    <Group gap="sm" wrap="wrap" align="flex-start">
                      <Code>[{k}]</Code>
                      <Text size="sm" c="dimmed">
                        {desc}
                      </Text>
                    </Group>
                  </Card>
                ))}
              </Stack>
            </ScrollArea>
          </Stack>
        )}
      </AppModal>

      <AppModal
        className="emergency-modal"
        opened={!!templatesProject}
        onClose={() => { if (!customTemplatesSaving) setTemplatesProject(null) }}
        title={`Шаблоны аварийного режима${templatesProject ? `: ${templatesProject.name}` : ''}`}
        size="lg"
        footer={
          <>
            <ActionButton
              action="cancel"
              onClick={() => setTemplatesProject(null)}
              disabled={customTemplatesSaving}
            />
            <ActionButton
              action="save"
              label="Сохранить шаблоны"
              loading={customTemplatesSaving}
              disabled={editorTemplates.loading || editorTemplates.error}
              onClick={() => void saveCustomTemplates()}
            />
          </>
        }
      >
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Эти шаблоны используются только в аварийном режиме и не зависят от шаблонов проекта.
          </Text>
          <Group>
            <Button
              variant="light"
              size="xs"
              leftSection={<IconPlus size={14} stroke={1.5} />}
              disabled={editorTemplates.loading || editorTemplates.error}
              onClick={() => addCustomTemplate('enable')}
            >
              Добавить шаблон включения
            </Button>
            <Button
              variant="light"
              size="xs"
              leftSection={<IconPlus size={14} stroke={1.5} />}
              disabled={editorTemplates.loading || editorTemplates.error}
              onClick={() => addCustomTemplate('disable')}
            >
              Добавить шаблон выключения
            </Button>
            <Button
              variant="light"
              size="xs"
              leftSection={<IconPlus size={14} stroke={1.5} />}
              disabled={editorTemplates.loading || editorTemplates.error}
              onClick={() => addCustomTemplate('message')}
            >
              Добавить шаблон промежуточного сообщения
            </Button>
          </Group>
          <Divider />
          {editorTemplates.loading ? <PageCenterLoader label="Загрузка шаблонов…" minHeight={120} /> : editorTemplates.error ? (
            <Alert color="red">Не удалось загрузить шаблоны. <Button variant="subtle" size="xs" onClick={editorTemplates.reload}>Повторить</Button></Alert>
          ) : customTemplates.length === 0 ? (
            <Text size="sm" c="dimmed">
              Пока нет кастомных шаблонов.
            </Text>
          ) : (
            <ScrollArea h={420} type="auto" offsetScrollbars>
              <Stack gap="md">
                <Text size="sm" fw={600}>
                  Шаблоны для включения аварийного режима
                </Text>
                {editorEnableTemplates.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    Нет шаблонов включения.
                  </Text>
                ) : null}
                {editorEnableTemplates.map((tpl, idx) => (
                  <Card key={tpl.id} withBorder padding="sm" radius="md">
                    <Stack gap="xs">
                      <Group justify="space-between" align="flex-end" wrap="wrap">
                        <TextInput
                          label={`Название #${idx + 1}`}
                          placeholder="Например: Массовая авария"
                          value={tpl.name}
                          onChange={(e) => updateCustomTemplate(tpl.id, { name: e.currentTarget.value })}
                          style={{ flex: 1 }}
                        />
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          aria-label="Удалить шаблон"
                          onClick={() => removeCustomTemplate(tpl.id)}
                        >
                          <IconTrash size={16} stroke={1.5} />
                        </ActionIcon>
                      </Group>
                      <Select label="Назначение" value={templateScope(tpl)} allowDeselect={false}
                        data={[{ value: 'enable', label: 'Включение аварии' }, { value: 'disable', label: 'Завершение аварии' }, { value: 'message', label: 'Промежуточное сообщение' }]}
                        onChange={(value) => updateCustomTemplate(tpl.id, { scope: value as EmergencyCustomTemplate['scope'] })} />
                      <Textarea
                        label="Текст шаблона"
                        placeholder="Текст сообщения для пользователей…"
                        minRows={4}
                        value={tpl.content}
                        onChange={(e) => updateCustomTemplate(tpl.id, { content: e.currentTarget.value })}
                      />
                    </Stack>
                  </Card>
                ))}
                <Divider />
                <Text size="sm" fw={600}>
                  Шаблоны для выключения аварийного режима
                </Text>
                {editorDisableTemplates.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    Нет шаблонов выключения.
                  </Text>
                ) : null}
                {editorDisableTemplates.map((tpl, idx) => (
                  <Card key={tpl.id} withBorder padding="sm" radius="md">
                    <Stack gap="xs">
                      <Group justify="space-between" align="flex-end" wrap="wrap">
                        <TextInput
                          label={`Название #${idx + 1}`}
                          placeholder="Например: Массовая авария"
                          value={tpl.name}
                          onChange={(e) => updateCustomTemplate(tpl.id, { name: e.currentTarget.value })}
                          style={{ flex: 1 }}
                        />
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          aria-label="Удалить шаблон"
                          onClick={() => removeCustomTemplate(tpl.id)}
                        >
                          <IconTrash size={16} stroke={1.5} />
                        </ActionIcon>
                      </Group>
                      <Select label="Назначение" value={templateScope(tpl)} allowDeselect={false}
                        data={[{ value: 'enable', label: 'Включение аварии' }, { value: 'disable', label: 'Завершение аварии' }, { value: 'message', label: 'Промежуточное сообщение' }]}
                        onChange={(value) => updateCustomTemplate(tpl.id, { scope: value as EmergencyCustomTemplate['scope'] })} />
                      <Textarea
                        label="Текст шаблона"
                        placeholder="Текст сообщения для пользователей…"
                        minRows={4}
                        value={tpl.content}
                        onChange={(e) => updateCustomTemplate(tpl.id, { content: e.currentTarget.value })}
                      />
                    </Stack>
                  </Card>
                ))}
                <Divider />
                <Text size="sm" fw={600}>
                  Шаблоны промежуточных сообщений
                </Text>
                {editorMessageTemplates.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    Нет шаблонов промежуточных сообщений.
                  </Text>
                ) : null}
                {editorMessageTemplates.map((tpl, idx) => (
                  <Card key={tpl.id} withBorder padding="sm" radius="md">
                    <Stack gap="xs">
                      <Group justify="space-between" align="flex-end" wrap="wrap">
                        <TextInput
                          label={`Название #${idx + 1}`}
                          placeholder="Например: Массовая авария"
                          value={tpl.name}
                          onChange={(e) => updateCustomTemplate(tpl.id, { name: e.currentTarget.value })}
                          style={{ flex: 1 }}
                        />
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          aria-label="Удалить шаблон"
                          onClick={() => removeCustomTemplate(tpl.id)}
                        >
                          <IconTrash size={16} stroke={1.5} />
                        </ActionIcon>
                      </Group>
                      <Select label="Назначение" value={templateScope(tpl)} allowDeselect={false}
                        data={[{ value: 'enable', label: 'Включение аварии' }, { value: 'disable', label: 'Завершение аварии' }, { value: 'message', label: 'Промежуточное сообщение' }]}
                        onChange={(value) => updateCustomTemplate(tpl.id, { scope: value as EmergencyCustomTemplate['scope'] })} />
                      <Textarea
                        label="Текст шаблона"
                        placeholder="Текст сообщения для пользователей…"
                        minRows={4}
                        value={tpl.content}
                        onChange={(e) => updateCustomTemplate(tpl.id, { content: e.currentTarget.value })}
                      />
                    </Stack>
                  </Card>
                ))}
              </Stack>
            </ScrollArea>
          )}
        </Stack>
      </AppModal>

      <AppModal
        className="emergency-modal"
        opened={!!disableModal}
        onClose={() => setDisableModal(null)}
        title="Выключение аварийного режима"
        size="md"
        footer={
          disableModal ? (
            <>
              <ActionButton action="cancel" onClick={() => setDisableModal(null)} disabled={disabling} />
              <Button
                color="green"
                leftSection={<IconPower size={16} stroke={1.5} />}
                loading={disabling}
                onClick={() => void onConfirmDisable()}
              >
                Выключить режим
              </Button>
            </>
          ) : undefined
        }
      >
        {disableModal ? (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              {disableModal.projectName}
            </Text>
            <Select
              label="Шаблон сообщения при выключении"
              data={disableTemplateSelectData}
              disabled={completionTemplates.loading || completionTemplates.error}
              placeholder={completionTemplates.loading ? 'Загрузка шаблонов…' : 'Выберите шаблон'}
              nothingFoundMessage="Нет шаблонов завершения"
              value={disableTemplateId}
              onChange={(value) => onDisableTemplateChange(value || '')}
              searchable
              allowDeselect={false}
              checkIconPosition="right"
            />
            {completionTemplates.error ? <Alert color="red">Не удалось загрузить шаблоны. <Button variant="subtle" size="xs" onClick={completionTemplates.reload}>Повторить</Button></Alert> : null}
            <Textarea
              label="Сообщение пользователям"
              description="Обязательно: уйдёт клиентам по затронутым обращениям"
              placeholder="Опишите выполненные работы и восстановление сервиса…"
              required
              minRows={4}
              value={completionMessage}
              onChange={(e) => setCompletionMessage(e.target.value)}
            />
            <div>
              <Text size="sm" fw={500} mb={8}>
                Действие с обращениями в аварии
              </Text>
              <Radio.Group
                value={disableAction || ''}
                onChange={(v) => setDisableAction(v as 'resolve' | 'close' | 'open')}
              >
                <Stack gap="sm">
                  <Radio value="resolve" label="Решить все обращения" />
                  <Radio value="close" label="Закрыть все обращения" />
                  <Radio value="open" label="Перевести в открытые" />
                </Stack>
              </Radio.Group>
            </div>
          </Stack>
        ) : null}
      </AppModal>

      <AppModal
        className="emergency-modal"
        opened={!!sendMessageModal}
        onClose={() => setSendMessageModal(null)}
        title="Промежуточное сообщение"
        size="md"
        footer={
          sendMessageModal ? (
            <>
              <ActionButton action="cancel" onClick={() => setSendMessageModal(null)} disabled={sendMessageSaving} />
              <Button
                leftSection={<IconSend size={16} stroke={1.5} />}
                loading={sendMessageSaving}
                onClick={() => void onConfirmSendMessage()}
              >
                Отправить
              </Button>
            </>
          ) : undefined
        }
      >
        {sendMessageModal ? (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              {sendMessageModal.projectName}
            </Text>
            <Select
              label="Аварийный шаблон"
              data={sendMessageTemplateSelectData}
              disabled={intermediateTemplates.loading || intermediateTemplates.error}
              placeholder={intermediateTemplates.loading ? 'Загрузка шаблонов…' : 'Выберите шаблон'}
              nothingFoundMessage="Нет промежуточных шаблонов"
              value={sendMessageTemplateId}
              onChange={(value) => onSendMessageTemplateChange(value || '')}
              searchable
              allowDeselect={false}
              checkIconPosition="right"
            />
            {intermediateTemplates.error ? <Alert color="red">Не удалось загрузить шаблоны. <Button variant="subtle" size="xs" onClick={intermediateTemplates.reload}>Повторить</Button></Alert> : null}
            <Textarea
              label="Сообщение пользователям"
              description="Уйдёт всем, кто уже попал в аварию на момент отправки"
              placeholder="Опишите текущий статус восстановления…"
              required
              minRows={4}
              value={sendMessageText}
              onChange={(e) => setSendMessageText(e.target.value)}
            />
          </Stack>
        ) : null}
      </AppModal>

      <AppModal
        className="emergency-modal"
        opened={!!editModal}
        onClose={() => setEditModal(null)}
        title="Редактирование аварийного режима"
        size="lg"
        footer={
          editModal ? (
            <>
              <ActionButton action="cancel" onClick={() => setEditModal(null)} disabled={editSaving} />
              <Button loading={editSaving} onClick={() => void onSaveEdit()}>
                Сохранить
              </Button>
            </>
          ) : undefined
        }
      >
        {editModal ? (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              {editModal.project_name ||
                projectsById.get(editModal.project_id)?.name ||
                editModal.project_id}
            </Text>
            <Alert icon={<IconAlertTriangle size={14} stroke={1.5} />} color="blue" variant="light" p="xs">
              <Text size="xs">
                Изменения сообщения и меток применяются к новым обращениям. Уже попавшие в аварию обращения не
                изменятся.
              </Text>
            </Alert>
            <Card withBorder padding="sm" radius="md">
              <EmergencyCurrentSettings
                ticketScope={editModal.ticket_scope}
                excludedTagIds={editModal.excluded_tag_ids}
                includedTagIds={editModal.included_tag_ids}
                tags={editTags}
              />
              <Group mt="sm" justify="flex-end">
                <Button
                  size="compact-sm"
                  variant="light"
                  color="orange"
                  loading={editApplyingUnassigned}
                  onClick={() => void onApplyUnassigned()}
                >
                  Добавить все не назначенные
                </Button>
              </Group>
              <Text size="xs" c="dimmed" mt={6}>
                Сразу добавит неназначенные new/open с учётом текущего фильтра меток (сначала сохранит метки,
                если вы их изменили).
              </Text>
            </Card>
            <Divider label="Изменить для новых обращений" labelPosition="center" />
            <Textarea
              label="Сообщение для пользователей"
              description="Будет отправлено новым обращениям, попадающим в аварию после сохранения"
              minRows={4}
              value={editMessage}
              onChange={(e) => setEditMessage(e.currentTarget.value)}
            />
            <Textarea
              label="Причина / заметка для анализа"
              placeholder="Что произошло, ссылка на инцидент, что проверить после завершения…"
              minRows={2}
              value={editNote}
              onChange={(e) => setEditNote(e.currentTarget.value)}
            />
            <TextInput
              label="Ссылка на тред"
              placeholder="https://..."
              value={editThreadUrl}
              onChange={(e) => setEditThreadUrl(e.currentTarget.value)}
            />
            <EmergencyTagFilterPanel
              mode={editTagFilterMode}
              onModeChange={setEditTagFilterMode}
              selectedTagIds={editTagFilterIds}
              onSelectedTagIdsChange={setEditTagFilterIds}
              tags={editTags}
              loading={editTagsLoading}
            />
          </Stack>
        ) : null}
      </AppModal>
    </PageShell>
  )
}
